/**
 * Service Selector Component for dev-tools
 * Allows users to select which service to interact with
 */
import React from "react";

import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";

import { AVAILABLE_SERVICES } from "../constants";
import { ServiceInfo } from "../types";

interface ServiceSelectorProps {
  selectedServiceId: string;
  onServiceChange: (serviceId: string) => void;
  loading?: boolean;
  services?: ServiceInfo[];
}

export const ServiceSelector: React.FC<ServiceSelectorProps> = ({
  selectedServiceId,
  onServiceChange,
  loading = false,
  services,
}) => {
  const handleChange = (event: SelectChangeEvent) => {
    onServiceChange(event.target.value);
  };

  // Use provided services or default to available services
  const serviceOptions =
    services ||
    AVAILABLE_SERVICES.map((service) => ({
      id: service.id,
      name: service.name,
      description: service.description,
      methodCount: undefined as number | undefined,
    }));

  // Don't render if there's only one service - it's not really a choice
  if (serviceOptions.length <= 1) {
    return null;
  }

  return (
    <FormControl size="small" sx={{ minWidth: 200, mr: 2 }}>
      <InputLabel id="service-selector-label">Service</InputLabel>
      <Select
        labelId="service-selector-label"
        id="service-selector"
        value={selectedServiceId}
        label="Service"
        onChange={handleChange}
        disabled={loading}
        sx={{
          "& .MuiSelect-select": {
            py: 1,
          },
        }}
      >
        {serviceOptions.map((service) => (
          <MenuItem
            key={service.id}
            value={service.id}
            sx={{
              py: 0.5,
              minHeight: "auto",
            }}
          >
            <Box>
              <Typography
                variant="body2"
                fontWeight="medium"
                sx={{ lineHeight: 1.2 }}
              >
                {service.name}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ lineHeight: 1 }}
              >
                {service.description}
                {service.methodCount && ` • ${service.methodCount} methods`}
              </Typography>
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
